import { money } from "@/plan-logic/lib/format";
import { ColumnHits, useColumnHover } from "./columns";
import { niceAxis, tickText } from "./scale";
import type { StackRow, StackSeries } from "./StackChart";

/**
 * The prototype's `groupChart` (v44): bars side by side in each row rather than stacked, so the
 * comparison is a height difference the eye reads directly (Debt's "Your debts, year by year":
 * today's path against Abi's plan, one debt at a time). The same column hover as `StackChart`;
 * in the gap the tip lists every bar of the column.
 */
export function GroupChart({
  rows,
  series,
  height = 300,
  barWidth = 20,
  label,
  note,
  testId,
}: {
  rows: StackRow[];
  series: StackSeries[];
  height?: number;
  barWidth?: number;
  label: string;
  note?: string;
  testId?: string;
}) {
  const W = 920;
  const H = height;
  const L = 70;
  const R = 16;
  const T = 16;
  const B = 46;
  const n = rows.length;
  const step = (W - L - R) / Math.max(1, n);
  const { wrap, tip, hover, onMove, onLeave } = useColumnHover({ n, L, step, W });
  if (!rows.length) return <p className="mut">Nothing to show for this part of the plan.</p>;
  const axis = niceAxis(
    0,
    Math.max(...rows.map((r) => Math.max(...series.map((s) => r.values[s.k] ?? 0)))) * 1.06,
  );
  const max = axis.hi;
  const bw = Math.min(barWidth, step * 0.36);
  const gap = Math.min(5, step * 0.08);
  const Y = (v: number) => H - B - (v / max) * (H - T - B);
  const every = Math.ceil(n / 8);
  const cx = (i: number) => L + step * i + step / 2;

  const hRow = hover ? rows[hover.i] : undefined;
  const hSeries = hover?.k ? series.find((s) => s.k === hover.k) : undefined;

  return (
    <div data-testid={testId} className="chwrap" ref={wrap}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="bars"
        role="img"
        aria-label={label}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        {axis.ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={Y(t)} y2={Y(t)} stroke="var(--line2)" />
            <text x={L - 9} y={Y(t) + 4} textAnchor="end" className="bx">
              {tickText(t, axis.step)}
            </text>
          </g>
        ))}
        <ColumnHits n={n} L={L} step={step} T={T} bottom={H - B} on={hover?.i} />
        {rows.map((r, i) => (
          <g key={`${r.label}-${i}`}>
            {series.map((s, k) => {
              const v = r.values[s.k] ?? 0;
              const x =
                cx(i) - (series.length * bw + (series.length - 1) * gap) / 2 + k * (bw + gap);
              const h = Math.max(v > 0 ? 1.5 : 0, H - B - Y(v));
              if (h <= 0) return null;
              return (
                <rect
                  key={s.k}
                  x={x.toFixed(1)}
                  y={(H - B - h).toFixed(1)}
                  width={bw.toFixed(1)}
                  height={h.toFixed(1)}
                  rx={Math.min(3, bw / 4).toFixed(1)}
                  fill={s.c}
                  data-i={i}
                  data-k={s.k}
                  className={hover?.i === i && hover.k === s.k ? "hv" : undefined}
                />
              );
            })}
          </g>
        ))}
        {rows.map((r, i) =>
          i % every === 0 || i === n - 1 ? (
            <text key={`l${i}`} x={cx(i).toFixed(1)} y={H - 26} textAnchor="middle" className="bx">
              {r.label}
            </text>
          ) : null,
        )}
      </svg>
      <div
        ref={tip}
        className={`chtip${hRow ? " on" : ""}`}
        aria-hidden="true"
        style={hover ? { left: hover.left, top: hover.top } : undefined}
        data-testid={testId ? `${testId}-tip` : undefined}
      >
        {hRow && (
          <>
            <b>{hRow.label}</b>
            {hSeries ? (
              <>
                {hSeries.n} <span className="v">{money(hRow.values[hSeries.k] ?? 0)}</span>
              </>
            ) : (
              // In the gap, every bar of the column: a sum of two paths would mean nothing.
              series.map((s) => (
                <span key={s.k} style={{ display: "block" }}>
                  {s.n} <span className="v">{money(hRow.values[s.k] ?? 0)}</span>
                </span>
              ))
            )}
          </>
        )}
      </div>
      <div className="legend" style={{ marginTop: 8 }}>
        {series.map((s) => (
          <span key={s.k}>
            <i className="lgd" style={{ background: s.c }} />
            {s.n}
          </span>
        ))}
      </div>
      {note && <p className="hint">{note}</p>}
    </div>
  );
}
